import { ApiError, describeError, FormError, useApi } from '@classgo/app-kit';
import type { TimetableCopyResult } from '@classgo/shared';
import { Button, Checkbox, Select } from '@classgo/ui';
import { type FormEvent, useState } from 'react';

import type { GroupView, SchoolYearView } from '../../academic/types';
import { useT } from '../../i18n';
import { timetableApi } from '../../timetable/api';
import { yearLabel } from './support';

/**
 * The dialog body that copies another group's week onto `target`: the source year (last year by
 * default) and a group of the sede in it (the one with the target's name by default, the same
 * group a year earlier). A target that already has blocks asks whether to replace them; the
 * API's 409 asks the same when the count was stale.
 */
export const CopyForm = ({
  target,
  targetCount,
  years,
  groups,
  onDone,
}: {
  target: GroupView;
  /**
   * How many blocks the target has now; more than zero shows the replace box from the start.
   */
  targetCount: number;
  years: readonly SchoolYearView[];
  /**
   * Every group of the sede, in any year.
   */
  groups: readonly GroupView[];
  onDone: (result: TimetableCopyResult, from: GroupView) => void;
}) => {
  const t = useT();
  const api = useApi();
  const targetYear = years.find((year) => year.id === target.schoolYearId);
  const earlier = years
    .filter((year) => targetYear !== undefined && year.startsOn < targetYear.startsOn)
    .sort((a, b) => b.startsOn.localeCompare(a.startsOn));
  const sourcesOf = (yearId: string) =>
    groups.filter((group) => group.schoolYearId === yearId && group.id !== target.id);
  const suggested = (yearId: string) => {
    const sources = sourcesOf(yearId);
    return sources.find((group) => group.name === target.name)?.id ?? sources[0]?.id ?? '';
  };
  const firstYear = earlier[0]?.id ?? target.schoolYearId;
  const [yearId, setYearId] = useState(firstYear);
  const [fromGroupId, setFromGroupId] = useState(() => suggested(firstYear));
  const [replace, setReplace] = useState(false);
  const [askReplace, setAskReplace] = useState(targetCount > 0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const sources = sourcesOf(yearId);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const from = sources.find((group) => group.id === fromGroupId);
    if (!from) {
      setError(t('timetable.copy.noSource'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await timetableApi(api).copy({
        fromGroupId: from.id,
        toGroupId: target.id,
        ...(replace ? { replace: true } : {}),
      });
      onDone(result, from);
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.code === 'CONFLICT') {
        setError(thrown.message);
        setAskReplace(true);
      } else {
        setError(describeError(t, thrown));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form noValidate className="space-y-4" onSubmit={(event) => void submit(event)}>
      <FormError message={error} />
      <p className="text-sm text-gray-600">{t('timetable.copy.help', { group: target.name })}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label={t('timetable.copy.year')}
          value={yearId}
          onChange={(event) => {
            setYearId(event.target.value);
            setFromGroupId(suggested(event.target.value));
          }}
          options={years.map((year) => ({
            value: year.id,
            label: yearLabel(year, t(`years.status.${year.status}`)),
          }))}
        />
        <Select
          label={t('timetable.copy.from')}
          value={fromGroupId}
          onChange={(event) => setFromGroupId(event.target.value)}
          options={
            sources.length > 0
              ? sources.map((group) => ({ value: group.id, label: group.name }))
              : [{ value: '', label: t('timetable.copy.noSource') }]
          }
        />
      </div>
      {askReplace ? (
        <Checkbox
          label={t('timetable.copy.replace', { group: target.name, count: targetCount })}
          checked={replace}
          onChange={(event) => setReplace(event.target.checked)}
        />
      ) : null}
      <div className="flex justify-end pt-2">
        <Button type="submit" loading={busy}>
          {t('timetable.copy.submit')}
        </Button>
      </div>
    </form>
  );
};
